import { useState, useEffect, useRef } from "react";
import FormGroup from "@mui/material/FormGroup";
import FormControl from "@mui/material/FormControl";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { AppState } from "../../AppState/appState";
import { useDispatch, useSelector } from "react-redux";
import { getTerminals } from "../../API/getTerminals";
import { saveTerminalMasterState } from "../../AppState/terminalMaster";
import { TerminalSelection } from "../../AppState/terminalSelection";
import { resetState } from "../../AppState/resetStateActions";
import { TTerminal } from "../../AppState/overagesFilterFormActions";
import { sortTerminals, userTerminal } from "../../helpers";
import Loader from "../loader/loader";
import { PopoutNotification } from "../../AppState/popoutNotification";
import NOTIFICATION_TYPE from "../../AppState/NotificationType";
import { ErrorMessages } from "../../constants";

type Props = {
  isDateSelectionAvailable?: boolean;
  isTerminalSelectionAvailable?: boolean;
};

const TerminalPicker: React.FC<Props> = (props: Props): JSX.Element => {
  const dispatch = useDispatch();
  const isLoggedIn = useSelector((state: AppState) => state.isLoggedIn);
  const currentTerminalValue = useSelector(
    (state: AppState) => state.activeTerminal
  );
  const [pageLoading, setPageLoading] = useState<boolean>(false);
  const terminalMasterData = useSelector(
    (state: AppState) => state.terminalMaster
  );
  const [terminals, setTerminals] = useState<any[]>(terminalMasterData);

  // This ref is used to track if the terminals have been initialized
  const hasInitializedTerminals = useRef(false);

  const selectDefaultTerminal = (list: TTerminal[] | undefined, fallback: TTerminal) => {
    const found = list?.find(
      (terminal) => terminal?.terminalId === userTerminal() && terminal?.region !== null
    );
    return found ?? fallback;
  };

  const handleTerminalsError = (error: any) => {
    if (
      error?.message === ErrorMessages.deploymentInprogressError ||
      error?.message === ErrorMessages.statusCodeNotFound
    )
      return;
    setPageLoading(false);
    console.error(error);
    dispatch(
      PopoutNotification(
        NOTIFICATION_TYPE.ERROR,
        typeof error === 'string' ? error : `Unable to retrieve terminal master data.`,
        Date.now().toString()
      )
    );
  };

  const getTerminalsAPI = () => {
    if (terminalMasterData.length > 0) {
      if (currentTerminalValue?.terminalId === "") {
        dispatch(TerminalSelection(selectDefaultTerminal(terminalMasterData, { terminalId: "", region: null })));
      }
      setTerminals([{ terminalId: "All", region: "" }, ...terminalMasterData]);
      return;
    }
    setPageLoading(true);
    getTerminals()
      .then(async (results: TTerminal[]) => {
        const terminals: TTerminal[] = sortTerminals(results);
        if (currentTerminalValue?.terminalId === "") {
          dispatch(TerminalSelection(selectDefaultTerminal(terminals, terminals[0])));
        }
        setTerminals([{ terminalId: "All", region: "" }, ...terminals]);
        dispatch(saveTerminalMasterState(terminals));
        setPageLoading(false);
      })
      .catch(handleTerminalsError);
  };

  const onTerminalChange = (event: any, selectedTerminal: any) => {
    dispatch(TerminalSelection(selectedTerminal));
    dispatch(resetState("TERMINAL_RESET"));
  };

  useEffect(() => {
    if (isLoggedIn && !hasInitializedTerminals.current) {
      getTerminalsAPI();
      hasInitializedTerminals.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  useEffect(() => {
    if (currentTerminalValue?.terminalId === "" && terminals.length > 0) {
      dispatch(
        TerminalSelection(
          terminals.find(
            (terminal) =>
              terminal?.terminalId === userTerminal() &&
              terminal?.region !== null
          ) ?? { terminalId: "", region: null }
        )
      );
    }
    if (
      !hasInitializedTerminals.current &&
      terminalMasterData.length < 1 &&
      isLoggedIn &&
      terminals.length
    ) {
      getTerminalsAPI();
      hasInitializedTerminals.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTerminalValue, terminals, isLoggedIn]);

  return (
    <>
      {currentTerminalValue?.terminalId &&
        terminals &&
        terminals.length > 0 && (
          <FormGroup className="filter-grp">
            <FormControl className="filter-control custom-user">
              <Autocomplete
                disablePortal={true}
                className="new-user-control terminal"
                id="terminal"
                data-testid="terminal-autocomplete"
                size="small"
                disableClearable={true}
                key={currentTerminalValue?.terminalId}
                options={terminals}
                value={currentTerminalValue}
                onChange={onTerminalChange}
                popupIcon={<KeyboardArrowDownIcon className="icon-style" />}
                isOptionEqualToValue={(option, value) =>
                  option.terminalId === value.terminalId
                }
                getOptionLabel={(option) =>
                  `${option.terminalId}${
                    option.region === null ? " (Region)" : ""
                  }`
                }
                renderOption={(props, option) => (
                  <li
                    {...props}
                    key={option.terminalId + "" + option.region}
                    className={option.region === null ?  "region" : "terminal" }
                  >
                    {`${option.terminalId}${
                      option.region === null ? " (Region)" : ""
                    }`}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    autoComplete="off"
                    {...params}
                    placeholder="Select Terminal"
                    data-testid="terminal-picker"
                    variant="outlined"
                    className="dropdown-list"
                    fullWidth
                    inputProps={{
                      ...params.inputProps,
                      style: { fontSize: "0.9rem" },
                    }}
                  />
                )}
              />
            </FormControl>{" "}
          </FormGroup>
        )}
      <Loader pageLoader={pageLoading}></Loader>
    </>
  );
};

export default TerminalPicker;
